import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, isEmpty, first, find, mergeWith, isNumber, some, pick } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { computeSortedContributors, positionSetContributorsSummaryText } from "helpers/contributors"
import { computeFactorCategories } from "helpers/factors"
import { getSelectedViewsFactors } from "selectors/factors"
import { getViewType, getPortfolioName, getShowNotHeld, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { makeGroupSort } from "helpers/contributorsSort"
import { computeAttributionMethod } from "helpers/risk"
import { getEffectiveSegment } from "modules/analyze-header/selectors/effective-segment"
import { computeMCTEAdjustedSegment } from "helpers/segments"
import { isCashOffsetContributor } from "helpers/assets"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

function getFactors(factors, mustAggregateFactors, selectedViewsFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }
  const result = aggregateFactorsSumBy(factorsClassification, factors, "value")

  if (isEmpty(selectedViewsFactors)) {
    return result
  }

  return chain(factors)
    .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
    .concat(result)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      acc.total += value.total
      acc.factorsTotal += value.attribution.summary.factors
      acc.specific += value.attribution.summary.specific

      const factors = getFactors(value.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
      merge(acc.factors, chain(factors)
        .keyBy("id")
        .mapValues(f => f.value)
        .value())

      const factorCategories = computeFactorCategories(factors, selectedViews)
      merge(acc.categories, factorCategories)

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      total: 0,
      factorsTotal: 0,
      specific: 0,
      factors: {},
      categories: {},
    })
    .value()
}

function mapContributors(contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const result = chain(contributors)
    .filter(contributor => showNotHeld || !contributor.notHeld)
    .map(contributor => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(contributor)
      const factors = getFactors(contributor.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)

      return {
        ...pick(contributor, [
          "id",
          "total",
          "country",
          "currency",
          "assetClass",
          "assetSubclass",
          "description",
          "notHeld",
          "bloombergTicker",
          "modelProviderId",
        ]),
        size: contributor.percentEquity,
        classification: get(contributor, "classification.name"),
        factorsTotal: contributor.attribution.summary.factors,
        specific: contributor.attribution.summary.specific,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification))
  }
  return result
}

const getContributorsData = state => get(state, "global.data.contributors.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getSelected = state => get(state, "modules.analyzeRiskDetails.selected", EMPTY_OBJECT)
const getOrder = state => get(state, "modules.analyzeRiskDetails.order", EMPTY_OBJECT)
const getGroupedContributorsData = state => get(state, "global.data.groupedRiskContributors.data.model.simulation.groupedRiskContributors")
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getRawSelectedGroup = (state, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate
const getSelectedViews = (state, props) => props.selectedViews

export const getGroupBy = (state, props) => get(props, "contributorsGrouping.id")

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  computeSelectedGroup,
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

export const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification],
  mapContributors,
)

const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => get(security, selectedField)),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

export const getSortedContributorsWithoutCashOffset = createSelector(
  [getSortedContributors],
  contributors => filter(contributors, contributor => !isCashOffsetContributor(contributor)),
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification],
  (data, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) =>
    map(data, group => {
      const factors = getFactors(group.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)

      return {
        ...group,
        total: group.attribution.summary.factors + group.attribution.summary.specific,
        factorsTotal: group.attribution.summary.factors,
        specific: group.attribution.summary.specific,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
        contributors: mapContributors(group.contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification),
      }
    }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    const groupFilter = selectedGroup ? group => selectedGroup.id === group.id : group => get(group, selectedField)
    return chain(data)
      .filter(groupFilter)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField) * 100,
        contributors: orderBy(group.contributors, [
          (security) => {
            switch (order.by) {
              case "name":
                return security.id
              case "equity":
                return order.abs ? Math.abs(security.size) : security.size
              default: {
                const value = get(security, selectedField)
                return order.abs && isNumber(value) ? Math.abs(value) : value
              }
            }
          },
        ], [order.dir]),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

export const getGroupedContributorsWithoutCashOffset = createSelector(
  [getSortedGroupContributors],
  groups => map(groups, (group) => ({
    ...group,
    contributors: filter(group.contributors, contributor => !isCashOffsetContributor(contributor)),
  })),
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: "Risk",
        portfolioName,
        selected,
        selectedDate,
        viewType,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: "Risk",
        portfolioName,
        selected,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      type: "Risk",
      portfolioName,
      selected,
      selectedDate,
      viewType,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField) * 100
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .filter(group => get(group, selectedField))
        .map(group => ({
          ...group,
          value: get(group, selectedField) * 100,
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)

const getIsActive = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)
export const getAttributionMethod = createSelector(
  [getIsActive],
  computeAttributionMethod,
)

export const getMCTEAdjustedSegment = createSelector(
  [getEffectiveSegment, getAttributionMethod, getIsActive],
  computeMCTEAdjustedSegment,
)
